import { extractNormalizedFilepathFromRecord } from "@arti/normalized-filepath";
import { isOneOf } from "@arti/utilities";
/**
 * Execution review schemas - shared between API and CLI.
 */
import { z } from "zod";

import { EXECUTION_REVIEW_SEVERITY_VALUES } from "../review/reviewSeverity";

export const EXECUTION_REVIEW_STATUS_VALUES = [
  "approved",
  "changes_requested",
] as const;
export type ExecutionReviewStatus =
  (typeof EXECUTION_REVIEW_STATUS_VALUES)[number];

export const EXECUTION_REVIEW_FEEDBACK_CATEGORY_VALUES = [
  "functional",
  "visual",
] as const;
export type ExecutionReviewFeedbackCategory =
  (typeof EXECUTION_REVIEW_FEEDBACK_CATEGORY_VALUES)[number];

export const executionReviewFeedbackEntrySchema = z
  .object({
    category: z
      .enum(EXECUTION_REVIEW_FEEDBACK_CATEGORY_VALUES)
      .describe(
        "Feedback category: functional for correctness/behavior issues, visual for UI/UX issues.",
      ),
    message: z
      .string()
      .describe("Specific issue or problem found that requires attention"),
    normalized_filepath: z
      .string()
      .min(1)
      .describe(
        "Repository-relative path associated with the feedback entry, normalized for comparisons.",
      )
      .nullable()
      .optional(),
    severity: z
      .enum(EXECUTION_REVIEW_SEVERITY_VALUES)
      .describe("Severity classification for the associated feedback entry"),
  })
  .strict();

export type ExecutionReviewFeedbackEntry = z.infer<
  typeof executionReviewFeedbackEntrySchema
>;

// Categorized review schema (functional + visual feedback)

/**
 * Input schema for LLM reviewer - only requires feedback array.
 * Statuses are derived deterministically from the feedback.
 */
export const categorizedReviewInputSchema = z
  .object({
    feedback: z
      .array(executionReviewFeedbackEntrySchema)
      .describe("List of issues found during review."),
  })
  .strict();

export type CategorizedReviewInput = z.infer<
  typeof categorizedReviewInputSchema
>;

/**
 * Full review schema with statuses - used internally after derivation.
 */
export const executionCategorizedReviewSchema = z
  .object({
    feedback: z.array(executionReviewFeedbackEntrySchema),
    functional_status: z.enum(EXECUTION_REVIEW_STATUS_VALUES),
  })
  .strict();

export type ExecutionCategorizedReview = z.infer<
  typeof executionCategorizedReviewSchema
>;

/**
 * Derive a full review with statuses from feedback-only input.
 * Statuses are determined purely from the feedback array contents.
 */
export function buildCategorizedReviewFromFeedback(
  input: CategorizedReviewInput,
): ExecutionCategorizedReview {
  const hasIssues = input.feedback.length > 0;

  return {
    feedback: input.feedback,
    functional_status: hasIssues ? "changes_requested" : "approved",
  };
}

export interface CategorizedReviewSnapshot {
  functionalStatus: ExecutionReviewStatus | null;
  functionalFeedback: ExecutionReviewFeedbackEntry[];
}

export function toCategorizedReviewSnapshot(
  functionalStatus: string | null | undefined,
  feedback: unknown,
): CategorizedReviewSnapshot {
  const entries = normalizeExecutionReviewFeedback(feedback);
  return {
    functionalFeedback: entries,
    functionalStatus: normalizeExecutionReviewStatus(functionalStatus),
  };
}

function normalizeExecutionReviewStatus(
  status: string | null | undefined,
): ExecutionReviewStatus | null {
  if (!status) {
    return null;
  }
  return isOneOf(status, EXECUTION_REVIEW_STATUS_VALUES) ? status : null;
}

function normalizeExecutionReviewFeedback(
  feedback: unknown,
): ExecutionReviewFeedbackEntry[] {
  if (!Array.isArray(feedback)) {
    return [];
  }
  return feedback
    .map((entry) => normalizeExecutionReviewFeedbackEntry(entry))
    .filter((entry): entry is ExecutionReviewFeedbackEntry => Boolean(entry));
}

function normalizeExecutionReviewFeedbackEntry(
  entry: unknown,
): ExecutionReviewFeedbackEntry | null {
  if (!entry || typeof entry !== "object") {
    return null;
  }
  const maybeMessage =
    "message" in entry ? (entry as { message?: unknown }).message : undefined;
  const maybeSeverity =
    "severity" in entry
      ? (entry as { severity?: unknown }).severity
      : undefined;
  const message = typeof maybeMessage === "string" ? maybeMessage.trim() : "";
  if (!message) {
    return null;
  }

  const severity = normalizeExecutionReviewSeverity(
    typeof maybeSeverity === "string" ? maybeSeverity : null,
  );
  const maybeCategory =
    "category" in entry
      ? (entry as { category?: unknown }).category
      : undefined;
  const category = normalizeExecutionReviewCategory(
    typeof maybeCategory === "string" ? maybeCategory : null,
  );
  const normalizedFilepath = extractNormalizedFilepathFromRecord(entry);

  return {
    category,
    message,
    severity,
    ...(normalizedFilepath ? { normalized_filepath: normalizedFilepath } : {}),
  };
}

function normalizeExecutionReviewSeverity(
  value: string | null,
): (typeof EXECUTION_REVIEW_SEVERITY_VALUES)[number] {
  if (!value) {
    return "medium";
  }
  return isOneOf(value, EXECUTION_REVIEW_SEVERITY_VALUES) ? value : "medium";
}

function normalizeExecutionReviewCategory(
  value: string | null,
): ExecutionReviewFeedbackCategory {
  if (!value) {
    return "functional";
  }
  return isOneOf(value, EXECUTION_REVIEW_FEEDBACK_CATEGORY_VALUES)
    ? value
    : "functional";
}

export const COMMIT_STATUS_VALUES = [
  "pushed",
  "no_changes",
  "rebased",
  "conflicts",
  "error",
] as const;
export type CommitStatus = (typeof COMMIT_STATUS_VALUES)[number];
export const commitStatusSchema = z.enum(COMMIT_STATUS_VALUES);

export interface ExecutionImplementationUiUxVisualChange {
  summary: string;
  assets?: string[] | undefined;
  notes?: string | undefined;
}

// FileChange type for review orchestration
export interface FileChange {
  path: string;
  status?: string;
  addedLines?: number | null;
  removedLines?: number | null;
  uiUxVisualChange?: {
    summary: string;
    assets?: string[] | null;
    notes?: string | null;
  } | null;
}
